import React from 'react';
import styled from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import { DropdownMenuContentProps } from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import Panel from '../../components/panel';
import { spacing } from '../../style';
import DotsLoader from '../../components/dots-loader';
import { SquareButton } from '../../components/button';
import { SquareButtonProps } from '../../components/button/square-button';
import { Flex, Div, BoxProps } from '../../components/box';
import Icon from '../../components/icon';
import { IconProps } from '../../components/icon/icon';
import { MenuPopoverProps } from '../../components/menu-popover';
import {
  CssUnitsAsResponsiveObject,
  getCssRuleWithResponsiveCalculation,
} from '../../utils/get-css-rule-with-responsive-calc';
import { AccessibleMenuItemProps } from '../../components/menu-popover/menu-item';
import { variants as buttonVariant } from '../../components/button/styles';
import { SkillMenu, SkillMenuButton, SkillMenuButtonProps } from './skill-menu';
import {
  SkillCategory,
  SkillCategoryProps,
  SkillLabel,
  SkillLabelProps,
  SkillProficiencyBars,
  SkillProficiencyBarsProps,
  SkillFamilies,
  SkillFamiliesProps,
} from './skill-components';
import { variants } from './style';
import { useSkillContext, SkillContextProvider } from './context';

export interface SkillProps {
  variant?: keyof typeof variants;
  width?: CssUnitsAsResponsiveObject<React.CSSProperties['width']>;
  maxWidth?: CssUnitsAsResponsiveObject<React.CSSProperties['maxWidth']>;
  ref?: any;
  gap?: keyof typeof spacing;
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  isHarmonized?: boolean;
  'data-testid'?: string;
}

type SkillActionButtonProps = Omit<SkillProps, 'variant'> & {
  iconVariant?: IconProps['variant'];
  icon: IconProps['name'];
} & SquareButtonProps;

const SkillActionButton = ({
  icon,
  'aria-hidden': ariaHidden = false,
  disabled,
  loading,
  variant,
  iconVariant = 'action-dark',
  ...props
}: SkillActionButtonProps) => {
  const { variant: parentVariant, disabled: disabledContext } = useSkillContext();
  const variantMapping: {
    [key in keyof typeof variants]: Extract<keyof typeof buttonVariant, 'secondary-clean' | 'secondary'>;
  } = {
    default: 'secondary-clean',
    gray100: 'secondary',
  };
  const isDisabled = disabled || disabledContext;

  return (
    <Div paddingHorizontal={'xxs'}>
      <SquareButton variant={variant || variantMapping[parentVariant]} disabled={isDisabled} size={'xs'} {...props}>
        {loading ? (
          <DotsLoader size={'xxs'} variant={'action'} />
        ) : (
          <Icon
            variant={iconVariant}
            style={{ fontSize: 16 }}
            name={icon}
            aria-hidden={ariaHidden}
            disabled={disabled}
          />
        )}
      </SquareButton>
    </Div>
  );
};

type SkillActionsProps = { children: React.ReactNode };

const SkillActions = ({ children }: SkillActionsProps) => {
  return (
    <>
      <Panel variant={'gray300'} width={2} height={'60%'} />
      {children}
    </>
  );
};

const SkillComponent = styled.div.withConfig({
  shouldForwardProp: prop => isPropValid(prop),
})<SkillProps>`
  display: flex;
  border-radius: ${spacing.xs};
  height: ${spacing.xl};
  width: ${props => props.width || 'max-content'};
  transition: all 160ms ease;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1rem;
  font-family: inherit;
  background-image: none; // Reset unusual Firefox-on-Android default style; see https://github.com/necolas/normalize.css/issues/214
  border: ${props =>
    `1px ${props.isHarmonized ? 'solid' : 'dashed'} ${variants[props.variant || 'default']?.borderColor}`};
  background-color: ${props => variants[props.variant || 'default']?.background};
  ${props =>
    getCssRuleWithResponsiveCalculation<React.CSSProperties['maxWidth']>({
      property: 'max-width',
      value: props.maxWidth,
    })};
`;

export const Skill: React.FunctionComponent<SkillProps> & {
  Actions: React.FunctionComponent<SkillActionsProps>;
  ActionButton: React.FunctionComponent<SkillActionButtonProps>;
  MenuButton: React.FunctionComponent<SkillMenuButtonProps>;
  Tag: React.FunctionComponent<BoxProps>;
  Menu: React.FunctionComponent<MenuPopoverProps>;
  Label: React.FunctionComponent<SkillLabelProps>;
  ProficiencyBars: React.FunctionComponent<SkillProficiencyBarsProps>;
  MenuItems: React.FunctionComponent<DropdownMenuContentProps>;
  Info: React.FunctionComponent<BoxProps>;
  MenuItem: React.FunctionComponent<AccessibleMenuItemProps>;
  CategoryTag: React.FunctionComponent<SkillCategoryProps>;
  Families: React.FunctionComponent<SkillFamiliesProps>;
} = ({ className, disabled, variant = 'default', isHarmonized = true, children, ...rest }: SkillProps) => (
  <SkillContextProvider disabled={disabled} variant={variant}>
    <SkillComponent
      disabled={disabled}
      variant={variant}
      className={classNames('skill-component', className)}
      isHarmonized={isHarmonized}
      {...rest}
    >
      {children}
    </SkillComponent>
  </SkillContextProvider>
);

export const SkillTag = ({ className, children, justifyContent, ...rest }: BoxProps) => (
  <Flex
    position={'relative'}
    style={{ padding: '12px' }}
    gap={'xs'}
    display={'flex'}
    className={classNames('skill-tag', className)}
    alignItems={'center'}
    height={'100%'}
    borderRadius={'md'}
    justifyContent={justifyContent}
    {...rest}
  >
    {children}
  </Flex>
);

Skill.Actions = SkillActions;
Skill.ActionButton = SkillActionButton;
Skill.MenuButton = SkillMenuButton;
Skill.Menu = SkillMenu;
Skill.Tag = SkillTag;
Skill.MenuItems = SkillMenu.Items;
Skill.MenuItem = SkillMenu.Item;
Skill.Label = SkillLabel;
Skill.ProficiencyBars = SkillProficiencyBars;
Skill.Info = SkillMenu.Info;
Skill.CategoryTag = SkillCategory;
Skill.Families = SkillFamilies;
